import React, { ReactNode, useEffect, useState } from 'react'
import { Button, Result, Spin } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect, useLocation } from 'react-router-dom'
import { getProfileUser } from '../../appRedux/actions/User/User'
import { userSignOut } from '../../appRedux/actions/Auth'
import IntlMessages from '../../util/IntlMessages'
import {
  HK_HOME_PATH,
  isCommonUserPath,
  isHkPathAllowed,
  isMlnHk,
} from '../../util/reportForHkAccess'
import {
  PermissionState,
  usePermissionGroups,
} from '../../util/usePermissionGroups'

const PROFILE_WAIT_MS = 15000

const PermissionBoundary = ({
  children,
}: {
  children: ReactNode
}): JSX.Element => {
  const permission = usePermissionGroups()
  const sessionVersion = useSelector(
    (state: PermissionState) => state.sysUser.profileSessionVersion
  )
  const currentUser = localStorage.getItem('username')
  const dispatch = useDispatch()
  const { pathname } = useLocation()
  const [timedOut, setTimedOut] = useState(false)
  const [retryVersion, setRetryVersion] = useState(0)

  useEffect(() => {
    setTimedOut(false)
    if (permission !== undefined) return
    const timeout = window.setTimeout(() => setTimedOut(true), PROFILE_WAIT_MS)
    return () => window.clearTimeout(timeout)
  }, [permission, currentUser, sessionVersion, retryVersion])

  if (isCommonUserPath(pathname)) return <>{children}</>

  if (permission === undefined) {
    if (!timedOut) {
      return (
        <Spin tip={<IntlMessages id="permission.profile.loading" />}>
          <div style={{ minHeight: 160 }} />
        </Spin>
      )
    }
    return (
      <Result
        status="error"
        title={<IntlMessages id="permission.profile.unavailable" />}
        extra={[
          <Button
            key="retry"
            type="primary"
            onClick={() => {
              setRetryVersion((value) => value + 1)
              dispatch(getProfileUser({ sub: currentUser }))
            }}
          >
            <IntlMessages id="permission.profile.retry" />
          </Button>,
          <Button key="logout" onClick={() => dispatch(userSignOut())}>
            <IntlMessages id="permission.profile.logout" />
          </Button>,
        ]}
      />
    )
  }

  if (isMlnHk(permission) && !isHkPathAllowed(pathname)) {
    return <Redirect to={HK_HOME_PATH} />
  }

  return <>{children}</>
}

export default PermissionBoundary
